import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Redirect, useHistory } from "react-router-dom";
import axios from "axios";
import Iframe from "react-iframe";
import {
  FaPoll,
  FaSyncAlt,
  FaHome,
  FaBookOpen,
  FaGraduationCap,
  FaPlus,
  FaTrophy,
  FaChartBar,
  FaTimes,
  FaCheck,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import MdlSelectSession from "../dialog/MdlSelectSession";
import BackButton from "../ChildComponent/BackButton";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const PRINTABLE_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR_PRINTABLE}ClassTest/`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;
const DEFAULT_IFRAME_HEIGHT = "420px";

const VIEW_ATS = "ats";
const VIEW_TTS = "tts";
const VIEW_RANK = "rank";

const REPORT_VIEWS = [
  { id: VIEW_ATS, label: "ATS", hint: "Average Test Score by subject", icon: FaChartBar },
  { id: VIEW_TTS, label: "TTS", hint: "Total Test Score sheet", icon: FaPoll },
  { id: VIEW_RANK, label: "Rank", hint: "Class rank by TTS", icon: FaTrophy },
];

function normalizeExamType(value) {
  const v = String(value || "").trim().toUpperCase().replace(/\s+/g, "_");
  return v === "CLASS_TEST" || v === "CLASSTEST" ? "CLASS_TEST" : "EXAM";
}

function classKey(item) {
  const section =
    !item?.section_id || item.section_id === "null" ? "No" : item.section_id;
  return `${item?.class_id || ""}|${section}`;
}

function isDummySection(value) {
  const v = String(value || "").trim().toLowerCase();
  return !v || v === "no" || v === "null" || v === "none" || v === "undefined";
}

function classLabel(item) {
  if (!item) return "Class";
  const cls = item.class_name || item.name || item.class_id || "Class";
  const sec = !isDummySection(item.section_name)
    ? item.section_name
    : !isDummySection(item.section_id)
      ? item.section_id
      : "";
  if (sec) return `${cls} - ${sec}`;
  return cls;
}

function examIncludesClass(exam, cls) {
  const list = exam?.participating_class_list;
  if (!Array.isArray(list) || !cls) return false;
  return list.some(
    (c) =>
      String(c.class_id) === String(cls.class_id) &&
      String(c.section_id || "No") === String(cls.section_id || "No")
  );
}

function uniqueClassesFromTests(tests) {
  const map = new Map();
  tests.forEach((exam) => {
    const list = Array.isArray(exam.participating_class_list)
      ? exam.participating_class_list
      : [];
    list.forEach((c) => {
      const key = classKey(c);
      if (!map.has(key)) {
        map.set(key, {
          class_id: c.class_id,
          section_id: !c.section_id || c.section_id === "null" ? "No" : c.section_id,
          class_name: c.class_name || c.name || c.class_id,
          section_name: c.section_name || "",
        });
      }
    });
  });
  return Array.from(map.values()).sort((a, b) =>
    classLabel(a).localeCompare(classLabel(b), undefined, { sensitivity: "base" })
  );
}

//Created By Cursor — parse exam start_date for ascending sort
function examDateMs(value) {
  const raw = String(value || "").trim();
  if (!raw) return Number.POSITIVE_INFINITY;
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const t = Date.parse(raw.slice(0, 10));
    return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
  }
  const dmy = raw.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})/);
  if (dmy) {
    const t = Date.parse(
      `${dmy[3]}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}`
    );
    return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
  }
  const t = Date.parse(raw);
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
}

//Created By Cursor — Include tests: date ASC, then name ASC (numeric)
function compareTestsAsc(a, b) {
  const da = examDateMs(a?.start_date);
  const db = examDateMs(b?.start_date);
  if (da !== db) return da - db;
  return String(a?.exam_name || "").localeCompare(
    String(b?.exam_name || ""),
    undefined,
    { sensitivity: "base", numeric: true }
  );
}

//Created By Cursor
function parseSubjectIds(raw) {
  return String(raw || "")
    .split(",")
    .map((id) => String(id).trim())
    .filter(Boolean);
}

//Created By Cursor — participating subject_ids for a class across exams (union, stable order)
function participatingSubjectIdsForClass(exams, cls) {
  if (!cls || !Array.isArray(exams) || exams.length === 0) return [];
  const ordered = [];
  const seen = new Set();
  exams.forEach((exam) => {
    const list = Array.isArray(exam.participating_class_list)
      ? exam.participating_class_list
      : [];
    const match = list.find(
      (c) =>
        String(c.class_id) === String(cls.class_id) &&
        String(c.section_id || "No") === String(cls.section_id || "No")
    );
    if (!match) return;
    parseSubjectIds(match.subject_ids).forEach((id) => {
      if (seen.has(id)) return;
      seen.add(id);
      ordered.push(id);
    });
  });
  return ordered;
}

//Created By Cursor — true if exam's participating subjects for class include subjectId
function examIncludesSubject(exam, cls, subjectId) {
  if (!subjectId || !cls || !exam) return false;
  const ids = participatingSubjectIdsForClass([exam], cls);
  return ids.some((id) => String(id) === String(subjectId));
}

function buildPrintableParams({
  selectedClass,
  sessionId,
  sessionName,
  schoolCode,
  includeRev,
  selectedSubject,
  showExamName,
  roundAts,
  onlyMarkFed,
  sortAts,
}) {
  return {
    school_code: schoolCode || "",
    session_id: sessionId,
    class_id: selectedClass.class_id,
    section_id: selectedClass.section_id || "No",
    class_name: classLabel(selectedClass),
    session_name: sessionName || "",
    system_code: process.env.REACT_APP_SYSTEM_CODE || "",
    subject_id: selectedSubject?.subject_id || "",
    subject_name: selectedSubject?.subject_name || "",
    show_exam_name: showExamName ? "1" : "0",
    round_ats: roundAts ? "1" : "0",
    only_mark_fed: onlyMarkFed ? "1" : "0",
    sort_ats: sortAts || "roll",
    //Created By Cursor — cache-buster when include list changes (reports read DB)
    include_rev: includeRev || "none",
  };
}

function getStaffId() {
  try {
    const staff = SessionManager.getStaffDetails();
    return staff?.staff_id || "";
  } catch (e) {
    return "";
  }
}

//Created By Cursor
function TestScore() {
  const loggedIn = localStorage.getItem("token") !== null;
  const history = useHistory();
  const schoolCode = SessionManager.getSchoolCode();

  const [bootLoading, setBootLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [examList, setExamList] = useState([]);
  const [subjectMaster, setSubjectMaster] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [sessionStatus, setSessionStatus] = useState("");
  const [isOpenSessionDialog, setIsOpenSessionDialog] = useState(false);
  const [selectedClassKey, setSelectedClassKey] = useState("");
  const [selectedView, setSelectedView] = useState(VIEW_ATS);
  const [selectedSubjectId, setSelectedSubjectId] = useState("");
  const [includedExamIds, setIncludedExamIds] = useState([]);
  const [showExamName, setShowExamName] = useState(true);
  const [showMarkFedOnly, setShowMarkFedOnly] = useState(true);
  const [roundAts, setRoundAts] = useState(false);
  const [sortAts, setSortAts] = useState("roll");
  const [markFedExamIds, setMarkFedExamIds] = useState([]);
  const [markFedBySubject, setMarkFedBySubject] = useState({});
  const [loadingMarkFed, setLoadingMarkFed] = useState(false);
  const [loadingAtsInclude, setLoadingAtsInclude] = useState(false);
  const [savingAtsInclude, setSavingAtsInclude] = useState(false);
  const [isOpenAddAtsDialog, setIsOpenAddAtsDialog] = useState(false);
  const [atsIncludeRev, setAtsIncludeRev] = useState(0);
  const [ttsIncludeGroups, setTtsIncludeGroups] = useState([]);
  const [loadingTtsInclude, setLoadingTtsInclude] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(DEFAULT_IFRAME_HEIGHT);
  const [iframeLoading, setIframeLoading] = useState(false);

  const classTests = useMemo(
    () => examList.filter((e) => normalizeExamType(e.exam_type) === "CLASS_TEST"),
    [examList]
  );

  const classList = useMemo(() => uniqueClassesFromTests(classTests), [classTests]);

  const selectedClass = useMemo(
    () => classList.find((c) => classKey(c) === selectedClassKey) || null,
    [classList, selectedClassKey]
  );

  const testsForClass = useMemo(() => {
    if (!selectedClass) return [];
    return classTests
      .filter((exam) => examIncludesClass(exam, selectedClass))
      .slice()
      .sort(compareTestsAsc);
  }, [classTests, selectedClass]);

  //Created By Cursor — subjects from all class tests' participating subject_ids
  const subjects = useMemo(() => {
    if (!selectedClass || testsForClass.length === 0) return [];
    const ids = participatingSubjectIdsForClass(testsForClass, selectedClass);
    if (ids.length === 0) return [];
    const nameById = new Map(
      subjectMaster.map((s) => [String(s.subject_id), s.subject_name || s.subject_id])
    );
    return ids.map((id) => ({
      subject_id: id,
      subject_name: nameById.get(String(id)) || id,
    }));
  }, [selectedClass, testsForClass, subjectMaster]);

  //Created By Cursor — ATS Include list: tests that include the selected subject
  const testsForInclude = useMemo(() => {
    if (!selectedClass || selectedView !== VIEW_ATS || !selectedSubjectId) {
      return [];
    }
    return testsForClass.filter((exam) =>
      examIncludesSubject(exam, selectedClass, selectedSubjectId)
    );
  }, [selectedClass, selectedView, selectedSubjectId, testsForClass]);

  const includedTests = useMemo(
    () =>
      testsForInclude.filter((exam) =>
        includedExamIds.includes(String(exam.exam_id))
      ),
    [testsForInclude, includedExamIds]
  );

  //Created By Cursor — ATS: ticked Include tests (table order)
  const orderedIncludedExamIds = useMemo(
    () => includedTests.map((t) => String(t.exam_id)),
    [includedTests]
  );

  //Created By Cursor — ATS preview uses included list (+ optional mark-fed)
  const reportExamIds = useMemo(() => {
    if (selectedView !== VIEW_ATS) {
      return orderedIncludedExamIds;
    }
    if (showMarkFedOnly && !loadingMarkFed) {
      const fed = new Set(markFedExamIds.map((id) => String(id)));
      return orderedIncludedExamIds.filter((id) => fed.has(id));
    }
    return orderedIncludedExamIds;
  }, [
    selectedView,
    showMarkFedOnly,
    loadingMarkFed,
    markFedExamIds,
    orderedIncludedExamIds,
  ]);

  const selectedSubject = useMemo(
    () => subjects.find((s) => String(s.subject_id) === String(selectedSubjectId)) || null,
    [subjects, selectedSubjectId]
  );

  const selectedViewMeta = useMemo(
    () => REPORT_VIEWS.find((v) => v.id === selectedView) || REPORT_VIEWS[0],
    [selectedView]
  );

  //Created By Cursor — TTS/Rank page: subject + included test names
  const ttsSubjectTestList = useMemo(() => {
    const nameBySubject = new Map(
      subjectMaster.map((s) => [String(s.subject_id), s.subject_name || s.subject_id])
    );
    const examById = new Map(
      testsForClass.map((exam) => [
        String(exam.exam_id),
        exam.exam_name || String(exam.exam_id),
      ])
    );
    return ttsIncludeGroups
      .map((group) => {
        const sid = String(group.subject_id);
        const fedSet = showMarkFedOnly
          ? new Set(
              markFedBySubject[sid.toLowerCase()] ||
                markFedBySubject[sid] ||
                []
            )
          : null;
        const tests = (group.exam_ids || [])
          .map((id) => String(id))
          .filter((id) => !fedSet || fedSet.has(id))
          .map((id) => ({
            exam_id: id,
            exam_name: examById.get(id) || id,
          }));
        return {
          subject_id: sid,
          subject_name: nameBySubject.get(sid) || sid,
          tests,
        };
      })
      .filter((item) => item.tests.length > 0);
  }, [
    ttsIncludeGroups,
    subjectMaster,
    testsForClass,
    showMarkFedOnly,
    markFedBySubject,
  ]);

  const getExamList = useCallback(async (session_id, { soft = false } = {}) => {
    if (!session_id) {
      setExamList([]);
      setBootLoading(false);
      setRefreshing(false);
      return;
    }
    if (soft) setRefreshing(true);
    else setBootLoading(true);
    if (!schoolCode) {
      setExamList([]);
      myToast.notify_warning("School code is missing.");
      setBootLoading(false);
      setRefreshing(false);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE()}getExamList.php`, {
        params: { school_code: schoolCode, session_id },
      });
      if (response.data?.api_status === "OK") {
        setExamList(response.data.data || []);
      } else {
        setExamList([]);
        myToast.notify_warning(response.data?.api_remark || "Could not load class tests.");
      }
    } catch (error) {
      console.error("Error fetching class tests:", error);
      setExamList([]);
      myToast.notify_warning("Network error loading class tests.");
    } finally {
      setBootLoading(false);
      setRefreshing(false);
    }
  }, [schoolCode]);

  //Created By Cursor
  const loadSubjectMaster = useCallback(async () => {
    if (!schoolCode) {
      setSubjectMaster([]);
      return;
    }
    setLoadingSubjects(true);
    try {
      const res = await axios.get(`${API_BASE()}getSubjectMasterList.php`, {
        params: { school_code: schoolCode },
      });
      const list =
        res.data?.api_status === "OK" && Array.isArray(res.data.data)
          ? res.data.data
          : [];
      setSubjectMaster(list);
    } catch (err) {
      console.error("[TestScore] subject master error", err);
      setSubjectMaster([]);
    } finally {
      setLoadingSubjects(false);
    }
  }, [schoolCode]);

  useEffect(() => {
    window.scrollTo(0, 0);
    try {
      const mSession = SessionManager.getCurrentSession();
      const sid = mSession?.[0]?.session_id || "";
      setSessionId(sid);
      setSessionName(mSession?.[0]?.session_name || "");
      setSessionStatus(mSession?.[0]?.status || "");
      getExamList(sid);
      loadSubjectMaster();
    } catch (e) {
      console.error(e);
      setBootLoading(false);
      myToast.notify_warning("Could not load current session.");
    }
  }, [getExamList, loadSubjectMaster]);

  useEffect(() => {
    if (subjects.length === 0) {
      if (selectedSubjectId) setSelectedSubjectId("");
      return;
    }
    const stillValid = subjects.some(
      (s) => String(s.subject_id) === String(selectedSubjectId)
    );
    if (!stillValid) {
      setSelectedSubjectId(subjects[0].subject_id);
    }
  }, [subjects, selectedSubjectId]);

  //Created By Cursor — load mark-fed exam ids for ATS subject, or all subjects for TTS/Rank
  useEffect(() => {
    if (!showMarkFedOnly || !selectedClass || !schoolCode) {
      setMarkFedExamIds([]);
      setMarkFedBySubject({});
      setLoadingMarkFed(false);
      return undefined;
    }

    if (selectedView === VIEW_ATS) {
      if (!selectedSubjectId) {
        setMarkFedExamIds([]);
        setLoadingMarkFed(false);
        return undefined;
      }
      const subjectExamIds = testsForClass
        .filter((exam) =>
          examIncludesSubject(exam, selectedClass, selectedSubjectId)
        )
        .map((exam) => String(exam.exam_id));

      if (subjectExamIds.length === 0) {
        setMarkFedExamIds([]);
        setLoadingMarkFed(false);
        return undefined;
      }

      let cancelled = false;
      setLoadingMarkFed(true);

      axios
        .get(`${API_BASE()}getClassTestMarkFedExams.php`, {
          params: {
            school_code: schoolCode,
            class_id: selectedClass.class_id,
            section_id: selectedClass.section_id || "No",
            subject_id: selectedSubjectId,
            exam_ids: subjectExamIds.join(","),
          },
        })
        .then((res) => {
          if (cancelled) return;
          const list =
            res.data?.api_status === "OK" && Array.isArray(res.data.data)
              ? res.data.data.map((id) => String(id))
              : [];
          setMarkFedExamIds(list);
        })
        .catch((err) => {
          if (cancelled) return;
          console.error("[TestScore] mark-fed exams error", err);
          setMarkFedExamIds([]);
        })
        .finally(() => {
          if (!cancelled) setLoadingMarkFed(false);
        });

      return () => {
        cancelled = true;
      };
    }

    if (selectedView !== VIEW_TTS && selectedView !== VIEW_RANK) {
      setMarkFedExamIds([]);
      setMarkFedBySubject({});
      setLoadingMarkFed(false);
      return undefined;
    }

    const examIds = [];
    const seen = new Set();
    ttsIncludeGroups.forEach((group) => {
      (group.exam_ids || []).forEach((id) => {
        const eid = String(id);
        if (!seen.has(eid)) {
          seen.add(eid);
          examIds.push(eid);
        }
      });
    });

    if (examIds.length === 0) {
      setMarkFedBySubject({});
      setLoadingMarkFed(false);
      return undefined;
    }

    let cancelled = false;
    setLoadingMarkFed(true);

    axios
      .get(`${API_BASE()}getClassTestMarkFedExams.php`, {
        params: {
          school_code: schoolCode,
          class_id: selectedClass.class_id,
          section_id: selectedClass.section_id || "No",
          exam_ids: examIds.join(","),
        },
      })
      .then((res) => {
        if (cancelled) return;
        const map = {};
        const rows =
          res.data?.api_status === "OK" && Array.isArray(res.data.data)
            ? res.data.data
            : [];
        rows.forEach((row) => {
          const sid = String(row.subject_id || "").toLowerCase();
          if (!sid) return;
          map[sid] = (row.exam_ids || []).map((id) => String(id));
        });
        setMarkFedBySubject(map);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[TestScore] TTS mark-fed exams error", err);
        setMarkFedBySubject({});
      })
      .finally(() => {
        if (!cancelled) setLoadingMarkFed(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    showMarkFedOnly,
    selectedView,
    selectedClass,
    selectedSubjectId,
    schoolCode,
    testsForClass,
    ttsIncludeGroups,
  ]);

  //Created By Cursor — load persisted ATS include list for selected subject
  useEffect(() => {
    if (
      selectedView !== VIEW_ATS ||
      !selectedClass ||
      !selectedSubjectId ||
      !sessionId ||
      !schoolCode
    ) {
      return undefined;
    }

    let cancelled = false;
    setLoadingAtsInclude(true);

    const subjectExamIds = testsForClass
      .filter((exam) =>
        examIncludesSubject(exam, selectedClass, selectedSubjectId)
      )
      .map((exam) => String(exam.exam_id));

    axios
      .get(`${API_BASE()}getClassTestAtsInclude.php`, {
        params: {
          school_code: schoolCode,
          session_id: sessionId,
          class_id: selectedClass.class_id,
          section_id: selectedClass.section_id || "No",
          subject_id: selectedSubjectId,
        },
      })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data;
        if (payload?.api_status === "OK" && payload.has_saved) {
          const saved = Array.isArray(payload.data)
            ? payload.data.map((id) => String(id))
            : [];
          const allowed = new Set(subjectExamIds);
          setIncludedExamIds(saved.filter((id) => allowed.has(id)));
        } else {
          setIncludedExamIds([]);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[TestScore] load ATS include error", err);
        setIncludedExamIds([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingAtsInclude(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    selectedView,
    selectedClass,
    selectedSubjectId,
    sessionId,
    schoolCode,
    testsForClass,
  ]);

  //Created By Cursor — TTS/Rank: load all subjects + included tests from DB
  useEffect(() => {
    if (
      (selectedView !== VIEW_TTS && selectedView !== VIEW_RANK) ||
      !selectedClass ||
      !sessionId ||
      !schoolCode
    ) {
      return undefined;
    }

    let cancelled = false;
    setLoadingTtsInclude(true);

    axios
      .get(`${API_BASE()}getClassTestAtsInclude.php`, {
        params: {
          school_code: schoolCode,
          session_id: sessionId,
          class_id: selectedClass.class_id,
          section_id: selectedClass.section_id || "No",
        },
      })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data;
        if (payload?.api_status === "OK" && Array.isArray(payload.data)) {
          setTtsIncludeGroups(
            payload.data.filter(
              (row) =>
                row &&
                row.subject_id &&
                Array.isArray(row.exam_ids) &&
                row.exam_ids.length > 0
            )
          );
        } else {
          setTtsIncludeGroups([]);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[TestScore] load TTS include groups error", err);
        setTtsIncludeGroups([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingTtsInclude(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    selectedView,
    selectedClass,
    sessionId,
    schoolCode,
    atsIncludeRev,
  ]);

  //Created By Cursor — persist ATS include exam_ids for subject
  const saveAtsInclude = useCallback(
    async (nextIds, action = "set") => {
      if (!selectedClass || !selectedSubjectId || !sessionId || !schoolCode) {
        return false;
      }
      setSavingAtsInclude(true);
      try {
        const body = new URLSearchParams({
          school_code: String(schoolCode),
          session_id: String(sessionId),
          class_id: String(selectedClass.class_id),
          section_id: String(selectedClass.section_id || "No"),
          subject_id: String(selectedSubjectId),
          staff_id: String(getStaffId()),
          action: String(action),
          exam_ids: nextIds.join(","),
        });
        const res = await axios.post(
          `${API_BASE()}saveClassTestAtsInclude.php`,
          body,
          { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
        );
        if (res?.data?.api_status !== "OK") {
          myToast.notify_warning(
            res?.data?.api_remark || "Could not save ATS tests."
          );
          return false;
        }
        if (Array.isArray(res.data.data)) {
          setIncludedExamIds(res.data.data.map((id) => String(id)));
        } else {
          setIncludedExamIds(nextIds);
        }
        setAtsIncludeRev((n) => n + 1);
        return true;
      } catch (err) {
        console.error("[TestScore] save ATS include error", err);
        myToast.notify_warning("Failed to save ATS tests.");
        return false;
      } finally {
        setSavingAtsInclude(false);
      }
    },
    [selectedClass, selectedSubjectId, sessionId, schoolCode]
  );

  const handleSelectClass = (cls) => {
    const key = classKey(cls);
    setSelectedClassKey(key);
    setSelectedView(VIEW_ATS);
    setSelectedSubjectId("");
    setIncludedExamIds([]);
  };

  const addExamToAts = async (examId) => {
    const id = String(examId);
    const next = includedExamIds.includes(id)
      ? includedExamIds
      : [...includedExamIds, id];
    setIncludedExamIds(next);
    await saveAtsInclude(next, "set");
  };

  const removeExamFromAts = async (examId, examName = "") => {
    const id = String(examId);
    const label = examName || `Test ${id}`;
    if (
      !window.confirm(
        `Remove "${label}" from ATS include list?`
      )
    ) {
      return;
    }
    const next = includedExamIds.filter((x) => x !== id);
    setIncludedExamIds(next);
    await saveAtsInclude(next, next.length === 0 ? "clear" : "set");
  };

  const clearAllAtsExams = async () => {
    if (
      !window.confirm(
        "Remove all included tests from ATS for this subject?"
      )
    ) {
      return;
    }
    setIncludedExamIds([]);
    await saveAtsInclude([], "clear");
  };

  const canShowIframe = useMemo(() => {
    if (!selectedClass || !sessionId) return false;
    if (selectedView === VIEW_ATS) {
      //Created By Cursor — show ATS once DB include list has tests (server filters mark-fed)
      return !!selectedSubjectId && orderedIncludedExamIds.length > 0;
    }
    return true;
  }, [
    selectedClass,
    sessionId,
    selectedView,
    selectedSubjectId,
    orderedIncludedExamIds,
  ]);

  const iframeUrl = useMemo(() => {
    if (!canShowIframe) return "";
    const params = buildPrintableParams({
      selectedClass,
      sessionId,
      sessionName,
      schoolCode,
      includeRev: `${atsIncludeRev}-${orderedIncludedExamIds.join(",") || "none"}-${showMarkFedOnly ? "fed" : "all"}`,
      selectedSubject: selectedView === VIEW_ATS ? selectedSubject : null,
      showExamName: selectedView === VIEW_ATS && showExamName,
      roundAts,
      onlyMarkFed: showMarkFedOnly,
      sortAts: selectedView === VIEW_ATS ? sortAts : "roll",
    });
    const qs = new URLSearchParams(params).toString();
    const files = {
      [VIEW_ATS]: "average_test_score.php",
      [VIEW_TTS]: "total_test_score.php",
      [VIEW_RANK]: "class_rank_by_tts.php",
    };
    return `${PRINTABLE_BASE()}${files[selectedView] || files[VIEW_ATS]}?${qs}`;
  }, [
    canShowIframe,
    selectedClass,
    sessionId,
    sessionName,
    schoolCode,
    atsIncludeRev,
    orderedIncludedExamIds,
    selectedSubject,
    selectedView,
    showExamName,
    roundAts,
    showMarkFedOnly,
    sortAts,
  ]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.iframeHeight) {
        setIframeHeight(`${event.data.iframeHeight}px`);
        setIframeLoading(false);
      }
    };
    window.addEventListener("message", handleMessage, false);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    if (!canShowIframe || !iframeUrl) {
      setIframeLoading(false);
      return undefined;
    }
    setIframeHeight(DEFAULT_IFRAME_HEIGHT);
    setIframeLoading(true);
    const safety = setTimeout(() => setIframeLoading(false), 25000);
    return () => clearTimeout(safety);
  }, [canShowIframe, iframeUrl, selectedView]);

  if (!loggedIn) return <Redirect to="/Login" />;
  if (bootLoading) return <MyLoader />;

  return (
    <div
      className="content payroll-page sm-page eamsw-page tsc-page"
      style={{
        background: COLORS.white,
        fontFamily:
          'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <Link to="/AdminMain/Academic/1" style={{ color: "#fff" }}>
                Academic
              </Link>
            </li>
            <li>
              <Link to="/AdminMain/ClassTestList" style={{ color: "#fff" }}>
                Class Tests
              </Link>
            </li>
            <li>
              <span style={{ color: "#fff" }}>Average Test Score (ATS)</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaPoll />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Average Test Score (ATS)</h1>
              <p className="sm-hero__sub">
                Select class, choose ATS / TTS / Rank, tick tests, then view report
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className="ct-session-pill"
              onClick={() => setIsOpenSessionDialog(true)}
              title="Change session"
            >
              <span className="ct-session-pill__label">Session</span>
              <span className="ct-session-pill__name">{sessionName || "Select"}</span>
              {sessionStatus ? (
                <span
                  className={`ct-session-pill__status${
                    String(sessionStatus).toLowerCase() === "active" || sessionStatus === "1"
                      ? " is-active"
                      : ""
                  }`}
                >
                  {sessionStatus}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              onClick={() => getExamList(sessionId, { soft: true })}
              disabled={refreshing || !sessionId}
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh"
              aria-label="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="eamsw-content tsc-content">
          <section className="eamsw-section-card tsc-soft-section" aria-label="Select class">
            <div className="eamsw-section-card__header">
              <h2 className="eamsw-section-card__title">
                <FaGraduationCap aria-hidden /> Select Class
              </h2>
              <span className="eamsw-section-card__count">{classList.length}</span>
            </div>
            <div className="eamsw-section-card__body">
              {classList.length === 0 ? (
                <div className="eamsw-empty">
                  <p className="eamsw-empty__title">No class tests found</p>
                  <Link
                    to="/AdminMain/ExamCreation/?type=CLASS_TEST"
                    className="ct-create-btn ct-create-btn--primary"
                    style={{ marginTop: 10 }}
                  >
                    <span className="ct-create-btn__icon" aria-hidden>
                      <FaPlus />
                    </span>
                    Create Class Test
                  </Link>
                </div>
              ) : (
                <div className="eamsw-chip-grid">
                  {classList.map((item) => (
                    <button
                      key={classKey(item)}
                      type="button"
                      className={`eamsw-chip${
                        selectedClassKey === classKey(item) ? " is-active" : ""
                      }`}
                      onClick={() => handleSelectClass(item)}
                    >
                      {classLabel(item)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>

          {selectedClass ? (
            <section
              className="eamsw-section-card tsc-soft-section"
              aria-label="Select report"
            >
              <div className="eamsw-section-card__header">
                <h2 className="eamsw-section-card__title">Select Report</h2>
                {selectedView !== VIEW_ATS ? (
                  <div className="tsc-header-actions">
                    <label className="tsc-opt-check">
                      <input
                        type="checkbox"
                        checked={showMarkFedOnly}
                        onChange={(e) => setShowMarkFedOnly(e.target.checked)}
                      />
                      <span>Show Mark Fed Test</span>
                    </label>
                    <label className="tsc-opt-check">
                      <input
                        type="checkbox"
                        checked={roundAts}
                        onChange={(e) => setRoundAts(e.target.checked)}
                      />
                      <span>Round ATS</span>
                    </label>
                  </div>
                ) : null}
              </div>
              <div className="eamsw-section-card__body">
                <div className="tsc-view-grid">
                  {REPORT_VIEWS.map((view) => {
                    const Icon = view.icon;
                    return (
                      <button
                        key={view.id}
                        type="button"
                        className={`tsc-view-chip${
                          selectedView === view.id ? " is-active" : ""
                        }`}
                        onClick={() => setSelectedView(view.id)}
                        title={view.hint}
                      >
                        <span className="tsc-view-chip__icon" aria-hidden>
                          <Icon />
                        </span>
                        <span className="tsc-view-chip__label">{view.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>
          ) : null}

          {selectedClass && selectedView === VIEW_ATS ? (
            <section className="eamsw-section-card tsc-soft-section" aria-label="Select subject">
              <div className="eamsw-section-card__header">
                <h2 className="eamsw-section-card__title">
                  <FaBookOpen aria-hidden /> Select Subject
                </h2>
                <span className="eamsw-section-card__count">{subjects.length}</span>
              </div>
              <div className="eamsw-section-card__body">
                {loadingSubjects ? (
                  <MyLoader />
                ) : subjects.length === 0 ? (
                  <p className="eamsw-empty__title">
                    No participating subjects for this class.
                  </p>
                ) : (
                  <div className="eamsw-chip-grid">
                    {subjects.map((sub) => (
                      <button
                        key={sub.subject_id}
                        type="button"
                        className={`eamsw-chip eamsw-chip--subject${
                          String(selectedSubjectId) === String(sub.subject_id)
                            ? " is-active"
                            : ""
                        }`}
                        onClick={() => {
                          setIncludedExamIds([]);
                          setIsOpenAddAtsDialog(false);
                          setSelectedSubjectId(sub.subject_id);
                        }}
                      >
                        {sub.subject_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </section>
          ) : null}

          {selectedClass && selectedView === VIEW_ATS && selectedSubjectId ? (
            <section className="eamsw-section-card tsc-soft-section" aria-label="Select tests">
              <div className="eamsw-section-card__header">
                <h2 className="eamsw-section-card__title">
                  Include tests
                  {selectedSubject ? ` · ${selectedSubject.subject_name}` : ""}
                </h2>
                <div className="tsc-header-actions">
                  <button
                    type="button"
                    className="tsc-all-btn tsc-all-btn--add"
                    onClick={() => setIsOpenAddAtsDialog(true)}
                    disabled={
                      testsForInclude.length === 0 ||
                      loadingAtsInclude ||
                      savingAtsInclude
                    }
                  >
                    <FaPlus aria-hidden /> Add Test In ATS
                  </button>
                  {includedExamIds.length > 0 ? (
                    <button
                      type="button"
                      className="tsc-all-btn tsc-all-btn--clear"
                      onClick={clearAllAtsExams}
                      disabled={loadingAtsInclude || savingAtsInclude}
                    >
                      Clear all
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="eamsw-section-card__body">
                {loadingAtsInclude ? (
                  <MyLoader />
                ) : testsForInclude.length === 0 ? (
                  <p className="eamsw-empty__title">
                    No tests include this subject.
                  </p>
                ) : (
                  <div className="tsc-included-list" aria-label="Included tests">
                    <div className="tsc-included-list__label">
                      Included tests ({includedTests.length})
                    </div>
                    {includedTests.length === 0 ? (
                      <p className="tsc-included-list__empty">
                        No tests included yet. Tap Add Test In ATS to choose tests.
                      </p>
                    ) : (
                      <ul className="tsc-included-list__rows">
                        {includedTests.map((exam, idx) => {
                          const id = String(exam.exam_id);
                          return (
                            <li key={id} className="tsc-included-list__row">
                              <div className="tsc-included-list__row-top">
                                <span className="tsc-included-list__idx">
                                  {idx + 1}.
                                </span>
                                <span className="tsc-included-list__name">
                                  {exam.exam_name || `Test ${idx + 1}`}
                                </span>
                              </div>
                              {exam.start_date ? (
                                <span className="tsc-included-list__date">
                                  {exam.start_date}
                                </span>
                              ) : null}
                              <button
                                type="button"
                                className="tsc-include-btn tsc-include-btn--remove tsc-included-list__remove"
                                onClick={() =>
                                  removeExamFromAts(
                                    id,
                                    exam.exam_name || `Test ${idx + 1}`
                                  )
                                }
                                disabled={savingAtsInclude}
                                aria-label={`Remove ${exam.exam_name || id}`}
                              >
                                Remove
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </section>
          ) : null}

          {selectedClass &&
          (selectedView === VIEW_TTS || selectedView === VIEW_RANK) ? (
            <section
              className="eamsw-section-card tsc-soft-section"
              aria-label="Included subjects and tests"
            >
              <div className="eamsw-section-card__header">
                <h2 className="eamsw-section-card__title">
                  Included subjects & tests
                </h2>
                <span className="eamsw-section-card__count">
                  {ttsSubjectTestList.length}
                </span>
              </div>
              <div className="eamsw-section-card__body">
                {loadingTtsInclude ? (
                  <MyLoader />
                ) : ttsSubjectTestList.length === 0 ? (
                  <p className="eamsw-empty__title">
                    No included tests yet. Add tests from ATS for each subject.
                  </p>
                ) : (
                  <div className="tsc-tts-sub-grid">
                    {ttsSubjectTestList.map((item) => (
                      <article
                        key={item.subject_id}
                        className="tsc-tts-sub-card"
                      >
                        <h3 className="tsc-tts-sub-card__title">
                          {item.subject_name}
                          <span className="tsc-tts-sub-card__count">
                            {item.tests.length}
                          </span>
                        </h3>
                        <ol className="tsc-tts-sub-card__tests">
                          {item.tests.map((test, idx) => (
                            <li key={test.exam_id}>
                              {idx + 1}. {test.exam_name}
                            </li>
                          ))}
                        </ol>
                      </article>
                    ))}
                  </div>
                )}
              </div>
            </section>
          ) : null}

          {selectedClass ? (
            <section
              className="eamsw-section-card tsc-soft-section tsc-report-preview"
              aria-label={selectedViewMeta.label}
            >
              <div className="eamsw-section-card__header">
                <h2 className="eamsw-section-card__title">
                  {selectedViewMeta.label === "ATS"
                    ? "Average Test Score (ATS)"
                    : selectedViewMeta.label === "TTS"
                      ? "Total Test Score (TTS)"
                      : "Class Rank by TTS"}
                </h2>
                {selectedView === VIEW_ATS ? (
                  <div className="tsc-header-actions tsc-header-actions--report">
                    <label className="tsc-opt-check">
                      <input
                        type="checkbox"
                        checked={showMarkFedOnly}
                        onChange={(e) => setShowMarkFedOnly(e.target.checked)}
                      />
                      <span>Show Mark Fed Test</span>
                    </label>
                    <label className="tsc-opt-check">
                      <input
                        type="checkbox"
                        checked={showExamName}
                        onChange={(e) => setShowExamName(e.target.checked)}
                      />
                      <span>Show Test Name</span>
                    </label>
                    <label className="tsc-opt-check">
                      <input
                        type="checkbox"
                        checked={roundAts}
                        onChange={(e) => setRoundAts(e.target.checked)}
                      />
                      <span>Round ATS</span>
                    </label>
                    <label className="tsc-sort-wrap">
                      <span className="tsc-sort-wrap__label">Sort ATS</span>
                      <select
                        className="tsc-sort-select"
                        value={sortAts}
                        onChange={(e) => setSortAts(e.target.value)}
                        aria-label="Sort ATS"
                      >
                        <option value="roll">Roll No.</option>
                        <option value="ats_desc">ATS High → Low</option>
                        <option value="ats_asc">ATS Low → High</option>
                      </select>
                    </label>
                  </div>
                ) : null}
              </div>
              <div className="eamsw-section-card__body tsc-tts-frame-wrap">
                {!canShowIframe ? (
                  <p className="eamsw-empty__title">
                    {selectedView === VIEW_ATS && !selectedSubjectId
                      ? "Select a subject and add tests to load ATS."
                      : selectedView === VIEW_ATS &&
                          orderedIncludedExamIds.length === 0
                        ? "Add at least one test with Add Test In ATS to load the report."
                        : "Loading report…"}
                  </p>
                ) : (
                  <>
                    {iframeLoading ? (
                      <div className="eamsw-loading-inline">
                        <MyLoader />
                      </div>
                    ) : null}
                    <Iframe
                      key={iframeUrl}
                      src={iframeUrl}
                      width="100%"
                      height={iframeHeight}
                      id="classTestReportFrame"
                      className="tsc-tts-iframe"
                      style={{ border: "none", overflow: "hidden" }}
                      scrolling="no"
                      onLoad={() => setIframeLoading(false)}
                    />
                  </>
                )}
              </div>
            </section>
          ) : null}
        </div>
      </div>

      {isOpenAddAtsDialog ? (
        <div className="tsc-modal" role="presentation">
          <button
            type="button"
            className="tsc-modal__backdrop"
            aria-label="Close dialog"
            onClick={() => setIsOpenAddAtsDialog(false)}
          />
          <div
            className="tsc-modal__panel"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="tsc-add-ats-title"
          >
            <div className="tsc-modal__hero">
              <div className="tsc-modal__hero-icon" aria-hidden>
                <FaPlus />
              </div>
              <div className="tsc-modal__hero-text">
                <h3 id="tsc-add-ats-title">Add Test In ATS</h3>
                <p>
                  {selectedSubject
                    ? `Choose class tests for ${selectedSubject.subject_name}`
                    : "Choose class tests to include in the ATS report"}
                </p>
              </div>
              <button
                type="button"
                className="tsc-modal__close"
                onClick={() => setIsOpenAddAtsDialog(false)}
                aria-label="Close"
              >
                <FaTimes aria-hidden />
              </button>
            </div>

            <div className="tsc-modal__meta">
              <span className="tsc-modal__chip">
                {testsForInclude.length} available
              </span>
              <span className="tsc-modal__chip tsc-modal__chip--in">
                {includedTests.length} included
              </span>
            </div>

            <div className="tsc-modal__body">
              {testsForInclude.length === 0 ? (
                <div className="tsc-modal__empty">
                  <FaBookOpen aria-hidden />
                  <p>No tests available for this subject.</p>
                </div>
              ) : (
                <ul className="tsc-modal__list">
                  {testsForInclude.map((exam, idx) => {
                    const id = String(exam.exam_id);
                    const alreadyIn = includedExamIds.includes(id);
                    return (
                      <li
                        key={id}
                        className={`tsc-modal__item${
                          alreadyIn ? " is-added" : ""
                        }`}
                      >
                        <span className="tsc-modal__item-idx" aria-hidden>
                          {idx + 1}
                        </span>
                        <div className="tsc-modal__item-main">
                          <div className="tsc-modal__item-name">
                            {exam.exam_name || `Test ${idx + 1}`}
                          </div>
                          <div className="tsc-modal__item-date">
                            {exam.start_date || "No date"}
                          </div>
                        </div>
                        {alreadyIn ? (
                          <span className="tsc-modal__badge">
                            <FaCheck aria-hidden /> Added
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="tsc-modal__add-btn"
                            onClick={() => addExamToAts(id)}
                            disabled={savingAtsInclude}
                          >
                            <FaPlus aria-hidden /> Add
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="tsc-modal__footer">
              <button
                type="button"
                className="tsc-modal__done"
                onClick={() => setIsOpenAddAtsDialog(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isOpenSessionDialog ? (
        <MdlSelectSession
          action={() => setIsOpenSessionDialog(false)}
          action2={(sid, sname, sstatus) => {
            setSessionId(sid);
            setSessionName(sname);
            setSessionStatus(sstatus || "");
            setIsOpenSessionDialog(false);
            setIsOpenAddAtsDialog(false);
            setSelectedClassKey("");
            setSelectedView(VIEW_ATS);
            setSelectedSubjectId("");
            setIncludedExamIds([]);
            getExamList(sid);
          }}
        />
      ) : null}
    </div>
  );
}

export default TestScore;
